import { Fragment, useState } from 'react'
import { CheckOutlined } from '@ant-design/icons'
import { Input, Popover, Row, Col, Popconfirm } from 'antd'
import { useAppDispatch, useAppSelector } from 'hooks'
import { addRespondingEntity } from 'Services/Settings/SettingsService'
import { RespondingEntity, setSettingLoading } from 'Slices/Settings/SettingsSlice'
import ColumnHeader from './ColumnHeader'
import { renderTranslation, stopPropagation } from 'Helpers/GenericHelpers'
import { TranslationKeys } from 'Constants/TranslationKeys'
import { SettingsApplicationSelectAll } from 'Constants/Settings'

interface AnswerColumnsHeaderProps {
  respondingEntities: RespondingEntity[]
  settingsName: string
  userCanEdit: boolean
  canAddRespondingEntity: boolean
}

const generateHeaders = (
  respondingEntities: RespondingEntity[] | null,
  settingsName: string,
  userCanEdit: boolean,
  isDefault: boolean = false,
  language: any,
) => {
  if (respondingEntities) {
    return respondingEntities.map((x: RespondingEntity, id) => {
      return (
        <Fragment key={id}>
          <ColumnHeader
            elementId={x.id}
            translation={x.name}
            respondingEntity={x}
            settingsName={settingsName}
            userCanEdit={userCanEdit}
            canBeEdited={x.canBeUpdated}
            canBeDeleted={x.canBeDeleted}
          />
        </Fragment>
      )
    })
  }

  if (isDefault) {
    return (
      <ColumnHeader
        elementId={SettingsApplicationSelectAll}
        translation={renderTranslation(language, TranslationKeys.columnHeaderAll)}
        respondingEntity={null}
        settingsName={settingsName}
        userCanEdit={userCanEdit}
        canBeEdited={false}
        canBeDeleted={false}
      />
    )
  }

  return null
}

const AddNewHeader = props => {
  const dispatch = useAppDispatch()

  const language = useAppSelector(state => state.present.language.value)
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)

  const handleAddPopconfirm = () => {
    if (inputValue) {
      dispatch(setSettingLoading(true))
      dispatch(addRespondingEntity(props.settingsName, inputValue))
      setOpen(!open)
    }
  }

  return (
    <Popover
      trigger={'click'}
      open={open}
      onOpenChange={() => {
        setOpen(!open)
      }}
      content={() => {
        return (
          <Row>
            <Col span={24}>
              <Input
                onPressEnter={e => {
                  stopPropagation(e)
                }}
                value={inputValue}
                onChange={e => {
                  setInputValue(e.target.value)
                }}
                width={50}
              />
              <Popconfirm
                title={renderTranslation(language, TranslationKeys.addColumnPopoverText)}
                onConfirm={handleAddPopconfirm}
                onCancel={stopPropagation}
                okText={renderTranslation(language, TranslationKeys.configurationDropdownPopYes)}
                cancelText={renderTranslation(language, TranslationKeys.configurationDropdownPopNo)}
                placement='bottom'
              >
                <CheckOutlined
                  className='generic-icon'
                  onClick={e => {
                    stopPropagation(e)
                  }}
                />
              </Popconfirm>
            </Col>
          </Row>
        )
      }}
      getPopupContainer={triggerNode => {
        return document.getElementById('page-content-settings')
      }}
    >
      {
        <div className={`big-table-cell header cursor-pointer`}>
          <span>{renderTranslation(language, TranslationKeys.columnHeaderAdd)} +</span>
        </div>
      }
    </Popover>
  )
}

function AnswersColumnsHeader(props: AnswerColumnsHeaderProps) {
  const language = useAppSelector(state => state.present.language.value)
  return (
    <div className='row header child'>
      {generateHeaders(null, props.settingsName, props.userCanEdit, true, language)}
      {generateHeaders(props.respondingEntities, props.settingsName, props.userCanEdit, false, language)}
      {props.userCanEdit && props.canAddRespondingEntity ? (
        <AddNewHeader settingsName={props.settingsName} /> //This render if there is a base setting? otherwise check for another way to render
      ) : null}
    </div>
  )
}

export default AnswersColumnsHeader
